import { API_BASE_URL } from "../../../../config/api";
import { getCompany } from "../../../../shared/data/storage/authStorage";

// API Error Class
class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const getStoredCompanyId = (): number => {
  const rawCompanyId = getCompany()?.companyId;
  const companyId = rawCompanyId ? Number.parseInt(String(rawCompanyId), 10) : NaN;

  if (!Number.isFinite(companyId) || companyId <= 0) {
    throw new APIError("Company details not found. Please sign in again.", 400);
  }

  return companyId;
};

// L2 Lead interface
export interface L2Lead {
  id: number;
  level_value: string;
}

// Get unique L2 leads response
export interface GetUniqueL2LeadsResponse {
  success: boolean;
  message?: string;
  data: L2Lead[];
}

// class_1 options response
export interface GetClass1OptionsResponse {
  success: boolean;
  message?: string;
  data: string[];
}

// Hierarchical level option (L1/L2/L3/...)
export interface LevelOption {
  level_number: number;
  level_value: string;
}

export interface GetLevelOptionsResponse {
  success: boolean;
  message?: string;
  data: LevelOption[];
}

/**
 * Get unique L2 leads for a company
 * @param companyId Company ID (defaults to stored company)
 * @returns Array of unique L2 leads with id and level_value
 */
export const getUniqueL2Leads = async (
  companyId: number = getStoredCompanyId()
): Promise<GetUniqueL2LeadsResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/registers/unique-l2-leads?company_id=${companyId}`
  );

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to retrieve L2 leads: ${response.statusText}`,
      response.status,
      data
    );
  }

  return data as GetUniqueL2LeadsResponse;
};

/**
 * Get available class_1 options for a company (e.g., BS, IS)
 */
export const getClass1Options = async (
  companyId: number = getStoredCompanyId()
): Promise<GetClass1OptionsResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/registers/class1-options?company_id=${companyId}`
  );

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to retrieve class_1 options: ${response.statusText}`,
      response.status,
      data
    );
  }

  return data as GetClass1OptionsResponse;
};

type GetLevelOptionsParams = {
  companyId?: number;
  class1: string;
  levelNumber: number;
  l1?: string;
  l2?: string;
  l3?: string;
  l4?: string;
  l5?: string;
};

/**
 * Get hierarchical level options (L1/L2/L3/...) for a given class_1 and selected path.
 */
export const getLevelOptions = async (
  params: GetLevelOptionsParams
): Promise<GetLevelOptionsResponse> => {
  const {
    companyId = getStoredCompanyId(),
    class1,
    levelNumber,
    l1,
    l2,
    l3,
    l4,
    l5,
  } = params;

  const search = new URLSearchParams();
  search.set("company_id", String(companyId));
  search.set("class_1", class1);
  search.set("level_number", String(levelNumber));
  if (l1) search.set("l1", l1);
  if (l2) search.set("l2", l2);
  if (l3) search.set("l3", l3);
  if (l4) search.set("l4", l4);
  if (l5) search.set("l5", l5);

  const response = await fetch(
    `${API_BASE_URL}/api/registers/level-options?${search.toString()}`
  );

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to retrieve level options: ${response.statusText}`,
      response.status,
      data
    );
  }

  return data as GetLevelOptionsResponse;
};

/**
 * Get level ID from lead path
 * @param leadPath The full lead path (e.g., "BS > Assets > Current Assets")
 * @param companyId Company ID (defaults to stored company)
 * @returns Level ID or null if not found
 */
export const getLeadLevelId = async (
  leadPath: string,
  companyId: number = getStoredCompanyId()
): Promise<number | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/registers/lead-level-id`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        lead_path: leadPath,
        company_id: companyId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || `Failed to get level ID: ${response.statusText}`,
        response.status,
        data
      );
    }

    return data.data?.level_id ?? null;
  } catch (error: any) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error.message || "Failed to get level ID",
      0,
      error
    );
  }
};

/**
 * Get lead path from level ID
 * @param levelId The level ID from cfo_mcoa_levels
 * @param companyId Company ID (defaults to stored company)
 * @returns Lead path (e.g., "BS > Assets > Current Assets") or null if not found
 */
export const getLeadPath = async (
  levelId: number,
  companyId: number = getStoredCompanyId()
): Promise<string | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/registers/lead-path`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        level_id: levelId,
        company_id: companyId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || `Failed to get lead path: ${response.statusText}`,
        response.status,
        data
      );
    }

    return data.data?.lead_path ?? null;
  } catch (error: any) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error.message || "Failed to get lead path",
      0,
      error
    );
  }
};

/**
 * Download register template for selected L2
 * @param l2LevelId The cfo_mcoa_levels.id for the selected L2
 * @param companyId Company ID (defaults to stored company)
 */
export const downloadRegisterTemplate = async (
  l2LevelId: number,
  companyId: number = getStoredCompanyId()
): Promise<void> => {
  const url = `${API_BASE_URL}/api/registers/download-template?l2_level_id=${l2LevelId}&company_id=${companyId}`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to download template' }));
      throw new APIError(
        errorData.message || `Download failed: ${response.statusText}`,
        response.status,
        errorData
      );
    }

    // Get the blob from the response
    const blob = await response.blob();

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'register_template.xlsx';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create a temporary URL and trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error: any) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error.message || 'Failed to download register template',
      0,
      error
    );
  }
};

// Upload register response types
export interface UploadError {
  row: number;
  ledger_name: string;
  field?: string;
  error: string;
  error_code: string;
}

export interface UploadRegisterResponse {
  success: boolean;
  phase: "validation" | "uploaded";
  message: string;
  data: {
    total_rows: number;
    valid_rows?: number;
    skipped_rows?: number;
    invalid_rows?: number;
    duplicate_in_file?: number;
    duplicate_in_db?: number;
    uploaded?: number;
    month?: string;
    year?: number;
    month_number?: number;
    l2_level_id?: number;
    preview?: Array<{
      row: number;
      ledger_name: string;
      amount: number;
    }>;
    skipped_preview?: Array<{
      row: number;
      ledger_name: string;
      reason: string;
    }>;
    errors?: UploadError[];
  };
}

/**
 * Upload register template Excel file
 * @param file Excel file to upload
 * @param phase "validation" or "confirm" (default: "validation")
 * @param companyId Company ID (defaults to stored company)
 * @returns Upload response with validation results or upload confirmation
 */
export const uploadRegister = async (
  file: File,
  phase: "validation" | "confirm" = "validation",
  companyId: number = getStoredCompanyId()
): Promise<UploadRegisterResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("phase", phase);
  formData.append("company_id", String(companyId));

  const response = await fetch(`${API_BASE_URL}/api/registers/upload`, {
    method: "POST",
    body: formData,
  });

  const data = await response.json();

  // Return the response directly (even if not ok) if it has the expected structure
  if (data.phase && data.data !== undefined) {
    return data as UploadRegisterResponse;
  }

  // If response is not ok and doesn't have expected structure, throw error
  if (!response.ok) {
    throw new APIError(
      data.message || `Upload failed: ${response.statusText}`,
      response.status,
      data
    );
  }

  return data as UploadRegisterResponse;
};

